<template>
    <form @submit.prevent="getAvailability()">
        <header v-if="!hideHeader">
            <legend class="block text-lg font-medium leading-7 text-slate-800">
                Room availability
            </legend>
            <p class="text-slate-500 text-sm mb-5">
                Choose available rooms from your searched dates. Use the
                availability calendar to quickly explore even more great
                options.
            </p>
        </header>
        <div class="grid grid-cols-12 gap-4">
            <div class="col-span-12 md:col-span-2">
                <UiInput
                    id="check-in-dater"
                    name="start_date"
                    label="Check in"
                    placeholder="Check in"
                    :options="{
                        icon: 'calendar',
                        hideLabel: true,
                    }"
                    :errors="formErrors"
                >
                    <template #input>
                        <flat-pickr
                            id="check-in-date"
                            :config="dateConfig"
                            class="relative block w-full rounded border border-slate-200 bg-white py-1.5 pl-10 text-gray-900 placeholder:text-gray-400 focus:z-10 placeholder:text-sm focus:ring-0 active:ring-0 focus:border-slate-800 active:border-slate-800"
                            name="check_in"
                            v-model="localCheckIn"
                            placeholder="Check in"
                        ></flat-pickr>
                    </template>
                </UiInput>
            </div>
            <div class="col-span-12 md:col-span-2">
                <UiInput
                    id="check-out-dater"
                    name="end_date"
                    label="Check out"
                    placeholder="Check out"
                    :options="{
                        icon: 'calendar',
                        hideLabel: true,
                    }"
                    :errors="formErrors"
                >
                    <template #input>
                        <flat-pickr
                            id="check-out-date"
                            :config="dateConfig"
                            class="relative block w-full rounded border border-slate-200 bg-white py-1.5 pl-10 text-gray-900 placeholder:text-gray-400 focus:z-10 placeholder:text-sm focus:ring-0 active:ring-0 focus:border-slate-800 active:border-slate-800"
                            name="check_in"
                            v-model="localCheckOut"
                            placeholder="Check out"
                        ></flat-pickr>
                    </template>
                </UiInput>
            </div>
            <div class="col-span-12 md:col-span-8">
                <button
                    type="submit"
                    class="w-full md:w-auto leading-tight text-sm text-white bg-orange-600 transition-colors border border-orange-600 shadow rounded px-4 py-2 hover:bg-red-400 active:bg-red-400 focus:outline-slate-200 focus:shadow disabled:opacity-50 disabled:bg-orange-600"
                >
                    {{ btnLabel }}
                </button>
            </div>
        </div>
    </form>
</template>
<script>
import { mapState, mapActions } from 'pinia';
import { useHotelsStore } from '@/store/hotels';
import { useCalendarStore } from '@/store/calendar';
import UiInput from '@/components/ui/inputs/ui-input.vue';
import dayjs from 'dayjs';
import FlatPickr from 'vue-flatpickr-component';

export default {
    props: {
        hideHeader: { type: Boolean, default: false },
    },
    components: {
        FlatPickr,
        UiInput,
    },
    data: () => ({
        dateConfig: {
            dateFormat: 'm/d/Y',
            altInput: true,
            altFormat: 'F d',
            minDate: 'today',
            maxDate: dayjs().add(13, 'month').format('MM/DD/YYYY'),
        },
        formErrors: {},
    }),
    watch: {
        checkIn(val) {
            if (val && !this.isValidDateRange) {
                let d = new Date(this.checkIn);
                this.setCheckOut(d.setDate(d.getDate() + 1));
            }
        },
    },
    computed: {
        ...mapState(useHotelsStore, ['property', 'calConfig', 'slug']),
        ...mapState(useCalendarStore, ['checkIn', 'checkOut']),
        localCheckIn: {
            get() {
                return this.checkIn;
            },
            set(val) {
                this.setCheckIn(val);
            },
        },
        localCheckOut: {
            get() {
                return this.checkOut;
            },
            set(val) {
                this.setCheckOut(val);
            },
        },
        isValidDateRange() {
            let fromDate = this.checkIn,
                toDate = this.checkOut,
                fdate = new Date(fromDate),
                tdate = new Date(toDate);

            if (fdate.valueOf() > tdate.valueOf()) {
                return false;
            }
            return true;
        },
        btnLabel() {
            return !this.checkIn || !this.checkOut ? 'Search' : 'Change search';
        },
    },
    methods: {
        ...mapActions(useCalendarStore, [
            'getEvents',
            'setSlug',
            'setDateRange',
            'setCheckInRange',
            'setCheckIn',
            'setCheckOut',
            'setUrl',
            'setView',
            'setTab',
        ]),
        resetDates() {
            this.setView({ name: 'month' });
            /*this.setDateRange({
                startDate: this.checkIn,
                endDate: this.checkOut,
            });*/
            this.setCheckInRange({
                checkIn: null,
                checkOut: null,
            });
            this.getEvents();
            this.$router.push(
                `/hotels/properties/${this.$route.params.slug}`,
                true
            );
        },
        getAvailability() {
            this.validate();
            if (this.errors.length === 0) {
                /* this.setDateRange({
                    startDate: this.checkIn,
                    endDate: this.checkOut,
                });*/
                this.setTab('list');
                this.$router.push(
                    `/hotels/properties/${this.$route.params.slug}?check_in=${this.checkIn}&check_out=${this.checkOut}`,
                    true
                );
            }
        },
        validate() {
            this.errors = [];
            this.formErrors = {};

            if (!this.checkIn) {
                this.formErrors.start_date = 'Please enter check in date.';
            }

            if (!this.checkOut) {
                this.formErrors.end_date = 'Please enter check out date.';
            }

            if (!this.checkIn || !this.checkOut) {
                this.errors.push({
                    message: 'Please enter start and end date.',
                });
            }

            this.validateDateRange();
        },
        validateDateRange() {
            if (!this.isValidDateRange) {
                this.formErrors.end_date =
                    'Please enter a check out date after check in date.';
                this.localCheckOut = null;
                this.errors.push({
                    message:
                        'Please enter a check out date after check in date.',
                });
            }
        },
    },
    mounted() {
        let checkIn = this.$route.query.check_in ?? null;
        let checkOut = this.$route.query.check_out ?? null;
        this.setCheckInRange({ checkIn: checkIn, checkOut: checkOut });
    },
};
</script>
